/**
 * 🌟 Arkturian Generic Settings Panel
 * Automatically renders UI from YAML configuration
 * 
 * Philosophy: The UI is a manifestation of consciousness, not hardcoded reality
 */

import React from 'react';
import { 
  SettingsSchema, 
  SettingDefinition, 
  SliderSettingDefinition,
  SelectSettingDefinition,
  ToggleSettingDefinition,
  InputSettingDefinition,
  ColorSettingDefinition,
  GroupSettingDefinition,
  SectionSettingDefinition
} from '@/config/settings-schema';
import { useSettingsStore } from '@/store/useSettingsStore';
import Panel from './Panel';

import Input from './Input';
import { ChevronDown, Play, Pause, RotateCcw } from 'lucide-react';

interface GenericSettingsPanelProps {
  schema?: SettingsSchema;
  className?: string;
}

/**
 * Individual Setting Renderers
 */
const SliderSetting: React.FC<{ definition: SliderSettingDefinition }> = ({ definition }) => {
  const { getValue, setValue, toggleAnimation, settings } = useSettingsStore();
  const value = getValue(definition.key) as number || definition.default;
  const isAnimating = settings[definition.key]?.isAnimating || false;

  const displayValue = definition.roundUI !== undefined 
    ? Number(value).toFixed(definition.roundUI)
    : value;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '0.5rem' 
      }}>
        <label style={{ 
          color: '#dbeafe', 
          fontSize: '0.875rem', 
          fontWeight: '500' 
        }}>
          {definition.label}
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ 
            color: '#94a3b8', 
            fontSize: '0.75rem',
            minWidth: '3rem',
            textAlign: 'right'
          }}>
            {displayValue}{definition.unit || ''}
          </span>
          {definition.animate && (
            <button
              onClick={() => toggleAnimation(definition.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '2rem',
                height: '2rem',
                background: isAnimating 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.2))'
                  : 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))',
                backdropFilter: 'blur(8px)',
                border: isAnimating 
                  ? '1px solid rgba(16, 185, 129, 0.4)'
                  : '1px solid rgba(96, 165, 250, 0.3)',
                borderRadius: '0.5rem',
                color: isAnimating ? '#6ee7b7' : '#93c5fd',
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: isAnimating
                  ? '0 3px 8px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                  : '0 3px 8px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (isAnimating) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(5, 150, 105, 0.3))';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.6)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                } else {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(59, 130, 246, 0.2))';
                  e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(96, 165, 250, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                }
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                if (isAnimating) {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.2))';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                } else {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))';
                  e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
                  e.currentTarget.style.boxShadow = '0 3px 8px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
                }
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={isAnimating ? 'Stop Animation' : 'Start Animation'}
            >
              {isAnimating ? <Pause size={14} /> : <Play size={14} />}
            </button>
          )}
        </div>
      </div>
      
      <input
        type="range"
        min={definition.min}
        max={definition.max}
        step={definition.step || 1}
        value={value}
        onChange={(e) => setValue(definition.key, Number(e.target.value))}
        style={{
          width: '100%',
          height: '0.5rem',
          background: 'rgba(96, 165, 250, 0.2)',
          borderRadius: '0.25rem',
          outline: 'none',
          cursor: 'pointer',
        }}
      />
      
      {definition.description && (
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          lineHeight: '1.4'
        }}>
          {definition.description}
        </p>
      )}
    </div>
  );
};

const SelectSetting: React.FC<{ definition: SelectSettingDefinition }> = ({ definition }) => {
  const { getValue, setValue } = useSettingsStore();
  const value = getValue(definition.key) || definition.default;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ 
        color: '#dbeafe', 
        fontSize: '0.875rem', 
        fontWeight: '500',
        display: 'block',
        marginBottom: '0.5rem'
      }}>
        {definition.label}
      </label>
      
      <select
        value={value as string}
        onChange={(e) => setValue(definition.key, e.target.value)}
        style={{
          width: '100%',
          padding: '0.5rem',
          background: 'rgba(30, 41, 59, 0.8)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '0.375rem',
          color: '#dbeafe',
          fontSize: '0.875rem',
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        {definition.options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      {definition.description && (
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          lineHeight: '1.4'
        }}>
          {definition.description}
        </p>
      )}
    </div>
  );
};

const ToggleSetting: React.FC<{ definition: ToggleSettingDefinition }> = ({ definition }) => {
  const { getValue, setValue } = useSettingsStore();
  const value = getValue(definition.key) as boolean ?? definition.default;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
        <div>
          <label style={{ 
            color: '#dbeafe', 
            fontSize: '0.875rem', 
            fontWeight: '500',
            display: 'block'
          }}>
            {definition.label}
          </label>
          {definition.description && (
            <p style={{ 
              color: '#64748b', 
              fontSize: '0.75rem', 
              marginTop: '0.25rem',
              lineHeight: '1.4'
            }}>
              {definition.description}
            </p>
          )}
        </div>
        
        <button
          onClick={() => setValue(definition.key, !value)}
          style={{
            width: '3.5rem',
            height: '2rem',
            background: value 
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.2))'
              : 'linear-gradient(135deg, rgba(71, 85, 105, 0.3), rgba(51, 65, 85, 0.2))',
            backdropFilter: 'blur(8px)',
            border: value 
              ? '1px solid rgba(16, 185, 129, 0.4)'
              : '1px solid rgba(96, 165, 250, 0.2)',
            borderRadius: '1rem',
            position: 'relative',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: value
              ? '0 4px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            if (value) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.4), rgba(5, 150, 105, 0.3))';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.6)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            } else {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            if (value) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.2))';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            } else {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(71, 85, 105, 0.3), rgba(51, 65, 85, 0.2))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.2)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)';
            }
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              background: value 
                ? 'linear-gradient(135deg, #ffffff, #f1f5f9)'
                : 'linear-gradient(135deg, #e2e8f0, #cbd5e1)',
              borderRadius: '50%',
              position: 'absolute',
              top: '0.125rem',
              left: value ? '1.875rem' : '0.125rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: value
                ? '0 4px 8px rgba(16, 185, 129, 0.3), 0 2px 4px rgba(0, 0, 0, 0.1)'
                : '0 2px 6px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          />
        </button>
      </div>
    </div>
  );
};

const InputSetting: React.FC<{ definition: InputSettingDefinition }> = ({ definition }) => {
  const { getValue, setValue } = useSettingsStore();
  const value = getValue(definition.key) as string || definition.default;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ 
        color: '#dbeafe', 
        fontSize: '0.875rem', 
        fontWeight: '500',
        display: 'block',
        marginBottom: '0.5rem'
      }}>
        {definition.label}
      </label>
      
      <Input
        value={value}
        onChange={(e) => setValue(definition.key, e.target.value)}
        placeholder={definition.placeholder}
        style={{ width: '100%' }}
      />
      
      {definition.description && (
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          lineHeight: '1.4'
        }}>
          {definition.description}
        </p>
      )}
    </div>
  );
};

const ColorSetting: React.FC<{ definition: ColorSettingDefinition }> = ({ definition }) => {
  const { getValue, setValue } = useSettingsStore();
  const value = getValue(definition.key) as string || definition.default;

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={{ 
        color: '#dbeafe', 
        fontSize: '0.875rem', 
        fontWeight: '500',
        display: 'block',
        marginBottom: '0.5rem'
      }}>
        {definition.label}
      </label>
      
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          type="color"
          value={value}
          onChange={(e) => setValue(definition.key, e.target.value)}
          style={{
            width: '3rem',
            height: '2rem',
            border: '1px solid rgba(96, 165, 250, 0.3)',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            background: 'transparent',
          }}
        />
        <Input
          value={value}
          onChange={(e) => setValue(definition.key, e.target.value)}
          style={{ flex: 1 }}
        />
      </div>
      
      {definition.description && (
        <p style={{ 
          color: '#64748b', 
          fontSize: '0.75rem', 
          marginTop: '0.25rem',
          lineHeight: '1.4'
        }}>
          {definition.description}
        </p>
      )}
    </div>
  );
};

const GroupSetting: React.FC<{ definition: GroupSettingDefinition }> = ({ definition }) => {
  const [isExpanded, setIsExpanded] = React.useState(definition.defaultExpanded ?? true);

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {definition.collapsible ? (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem 1.25rem',
            background: isExpanded
              ? 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(59, 130, 246, 0.1))'
              : 'linear-gradient(135deg, rgba(71, 85, 105, 0.2), rgba(51, 65, 85, 0.1))',
            backdropFilter: 'blur(12px)',
            border: isExpanded
              ? '1px solid rgba(96, 165, 250, 0.3)'
              : '1px solid rgba(96, 165, 250, 0.2)',
            borderRadius: '0.75rem',
            color: '#dbeafe',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '1rem',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: isExpanded
              ? '0 6px 20px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              : '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            position: 'relative',
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            if (isExpanded) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.15))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.4)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(96, 165, 250, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
            } else {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(59, 130, 246, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            if (isExpanded) {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.15), rgba(59, 130, 246, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            } else {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(71, 85, 105, 0.2), rgba(51, 65, 85, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.2)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.05)';
            }
            e.currentTarget.style.transform = 'translateY(0px)';
          }}
        >
          <span>{definition.label}</span>
          <div style={{
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}>
            <ChevronDown size={20} />
          </div>
        </button>
      ) : (
        <h3 style={{
          color: '#dbeafe',
          fontSize: '1rem',
          fontWeight: '600',
          marginBottom: '1rem',
          paddingBottom: '0.5rem',
          borderBottom: '1px solid rgba(96, 165, 250, 0.2)',
        }}>
          {definition.label}
        </h3>
      )}
      
      {definition.description && (
        <p style={{ 
          color: '#94a3b8', 
          fontSize: '0.875rem', 
          marginBottom: '1rem',
          lineHeight: '1.5'
        }}>
          {definition.description}
        </p>
      )}
      
      {isExpanded && (
        <div style={{ paddingLeft: definition.collapsible ? '1rem' : '0' }}>
          {definition.children.map((child) => (
            <SettingRenderer key={child.key} definition={child} />
          ))}
        </div>
      )}
    </div>
  );
};

const SectionSetting: React.FC<{ definition: SectionSettingDefinition }> = ({ definition }) => {
  return (
    <div style={{ marginBottom: '2rem' }}>
      <h2 style={{
        color: '#dbeafe',
        fontSize: '1.25rem',
        fontWeight: '700',
        marginBottom: '0.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        {definition.label}
      </h2>
      
      {definition.description && (
        <p style={{ 
          color: '#94a3b8', 
          fontSize: '0.875rem', 
          marginBottom: '1.5rem',
          lineHeight: '1.5'
        }}>
          {definition.description}
        </p>
      )}
      
      <div>
        {definition.children.map((child) => (
          <SettingRenderer key={child.key} definition={child} />
        ))}
      </div>
    </div>
  );
};

/**
 * Main Setting Renderer - routes to appropriate component
 */
const SettingRenderer: React.FC<{ definition: SettingDefinition }> = ({ definition }) => {
  switch (definition.type) {
    case 'slider':
      return <SliderSetting definition={definition} />;
    case 'select':
      return <SelectSetting definition={definition} />;
    case 'toggle':
      return <ToggleSetting definition={definition} />;
    case 'input':
      return <InputSetting definition={definition} />;
    case 'color':
      return <ColorSetting definition={definition} />;
    case 'group':
      return <GroupSetting definition={definition} />;
    case 'section':
      return <SectionSetting definition={definition} />;
    default:
      return null;
  }
};

/**
 * Main Generic Settings Panel Component
 */
const GenericSettingsPanel: React.FC<GenericSettingsPanelProps> = ({ 
  schema, 
  className = '' 
}) => {
  const { resetToDefaults, exportSettings, importSettings } = useSettingsStore();

  if (!schema) {
    return (
      <Panel className={className}>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
          <p>No settings schema loaded</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Load a YAML configuration to begin
          </p>
        </div>
      </Panel>
    );
  }

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const content = e.target?.result as string;
            importSettings(content);
          } catch (error) {
            console.error('Failed to import settings:', error);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleExport = () => {
    const settings = exportSettings();
    const blob = new Blob([settings], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'arkturian-settings.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Panel className={className} padding="lg">
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          color: '#dbeafe',
          fontSize: '1.5rem',
          fontWeight: '700',
          marginBottom: '0.5rem',
        }}>
          {schema.name}
        </h1>
        
        {schema.description && (
          <p style={{ 
            color: '#94a3b8', 
            fontSize: '0.875rem',
            lineHeight: '1.5'
          }}>
            {schema.description}
          </p>
        )}
        
        <div style={{ 
          display: 'flex', 
          gap: '0.75rem', 
          marginTop: '1.5rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={resetToDefaults}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.1))',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '0.75rem',
              color: '#fca5a5',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.2))';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(239, 68, 68, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
          >
            <RotateCcw size={16} />
            Reset to Defaults
          </button>
          
          <button
            onClick={handleExport}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(96, 165, 250, 0.3)',
              borderRadius: '0.75rem',
              color: '#93c5fd',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.3), rgba(59, 130, 246, 0.2))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.5)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(96, 165, 250, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(96, 165, 250, 0.2), rgba(59, 130, 246, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(96, 165, 250, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export Settings
          </button>
          
          <button
            onClick={handleImport}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.1))',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '0.75rem',
              color: '#6ee7b7',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.2))';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.5)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(16, 185, 129, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.1))';
              e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Import Settings
          </button>
        </div>
      </div>

      {/* Settings */}
      <div>
        {Object.values(schema.settings).map((setting) => (
          <SettingRenderer key={setting.key} definition={setting} />
        ))}
      </div>
    </Panel>
  );
};

export default GenericSettingsPanel; 